import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, InlineNotification, Modal, Select, SelectItem, Stack, TextInput } from '@carbon/react';
import { EmployeeRow } from 'src/features/employees';
import { useInviteEmployee } from 'src/hooks/useEmployeeInvitations';
import { useInvitations } from 'src/hooks/useInvitations';
import { RestaurantScope, tenantEmployeeUpdate } from 'src/services/apis/tenantRequests';
import { EmployeeUpdateDto } from 'src/services/apis/types';
import { accountStatusOf, AccountStatus } from 'src/utils/accountStatus';

interface EmployeeInviteModalProps {
  row: EmployeeRow | null;
  scope: RestaurantScope;
  onClose: () => void;
}

/**
 * One dialog covers invite, resend and cancel — which shape it renders is derived from
 * `accountStatusOf(row)`, never a separate prop, so the row action and the dialog can never
 * disagree about what state an employee is in.
 *
 * Email handling (spec decision left to this task): the dialog COLLECTS the email and
 * persists it via `tenantEmployeeUpdate` before inviting, rather than requiring a prior trip
 * through the edit form. That keeps "Invite" a single action from the row — but the save
 * happens strictly before `inviteEmployee` runs, so an address is always on the employment
 * record before better-auth ever sends it (never invite an address that was never saved).
 */
const EmployeeInviteModal = React.memo((props: EmployeeInviteModalProps) => {
  const { row, scope, onClose } = props;
  const { t } = useTranslation();
  const { invite, cancel, organizationId } = useInviteEmployee();
  const { data: invitations } = useInvitations(organizationId);

  const existingEmail = row?.user?.email ?? row?.email ?? '';
  // Seeded once per mount. The call site keys this component on the row id, so a different
  // employee gets a fresh instance rather than an effect that re-seeds an existing one.
  const [email, setEmail] = useState(existingEmail);
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [saveError, setSaveError] = useState<string | null>(null);

  const status: AccountStatus = row ? accountStatusOf(row) : 'none';
  const isActive = status === 'active';
  const isResend = status === 'invited' || status === 'expired';
  const trimmedEmail = email.trim();

  // better-auth has no notion of "which employee" — matching is by email, against the one
  // still-pending invitation (a resend cancels and re-invites, so there is at most one).
  const pendingInvitation = invitations?.find(
    (invitation) => invitation.status === 'pending' && invitation.email.toLowerCase() === existingEmail.toLowerCase()
  );

  const busy = invite.isPending || cancel.isPending;
  const mutationError = (invite.error as Error | null)?.message ?? (cancel.error as Error | null)?.message ?? null;
  const errorMessage = saveError ?? mutationError;

  const heading = isResend ? t('actions.resendInvite') : t('actions.invite');
  const employeeName = row ? [row.firstName, row.lastName].filter(Boolean).join(' ') : '';

  async function handleSubmit() {
    if (!row || isActive || !trimmedEmail) {
      return;
    }
    setSaveError(null);
    try {
      if (trimmedEmail !== existingEmail) {
        const payload: EmployeeUpdateDto = {
          id: row.id,
          activated: row.activated,
          extraBonus: row.extraBonus,
          partTime: row.partTime,
          restaurantBranchId: row.restaurantBranchId,
          restaurantId: row.restaurantId,
          role: row.role as 'M' | 'E',
          salary: row.salary,
          salaryUnit: row.salaryUnit as 'M' | 'H',
          email: trimmedEmail,
        };
        await tenantEmployeeUpdate(scope, row.id, payload);
      }
    } catch (error) {
      setSaveError((error as Error).message);
      return;
    }
    await invite.mutateAsync({ employeeId: row.id, email: trimmedEmail, role });
    onClose();
  }

  async function handleCancelInvitation() {
    if (!row || !pendingInvitation) {
      return;
    }
    await cancel.mutateAsync({ employeeId: row.id, invitationId: pendingInvitation.id });
    onClose();
  }

  return (
    <Modal
      open={!!row}
      size="sm"
      modalHeading={heading}
      modalLabel={employeeName || t('employee.account.none')}
      primaryButtonText={busy ? `${t('common.loading')}...` : heading}
      secondaryButtonText={t('actions.cancel')}
      primaryButtonDisabled={busy || isActive || !trimmedEmail}
      onRequestClose={onClose}
      onSecondarySubmit={onClose}
      onRequestSubmit={handleSubmit}
    >
      <Stack gap={5}>
        {errorMessage && (
          <InlineNotification kind="error" lowContrast hideCloseButton title="" subtitle={errorMessage} />
        )}
        {isActive ? (
          <p>{t('employee.invite.alreadyActive')}</p>
        ) : (
          <>
            <TextInput
              id="invite-employee-email"
              type="email"
              labelText={t('common.email')}
              placeholder={t('common.email')}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Select
              id="invite-employee-role"
              labelText={t('common.role')}
              value={role}
              onChange={(event) => setRole(event.target.value as 'member' | 'admin')}
            >
              <SelectItem value="member" text={t('orgSettings.members.member')} />
              <SelectItem value="admin" text={t('orgSettings.members.admin')} />
            </Select>
            {isResend && (
              <Button
                kind="danger--ghost"
                size="sm"
                disabled={!pendingInvitation || cancel.isPending}
                onClick={handleCancelInvitation}
              >
                {t('employee.invite.cancelInvitation')}
              </Button>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
});

EmployeeInviteModal.displayName = 'EmployeeInviteModal';

export default EmployeeInviteModal;
